import axios from 'axios';
import getAccesstokenNameByRoute from './getAccesstokenNameByRoute';
import { getTenantKey } from '../tenant-context';

// JWT 점진 채택: access(JWT) 만료(401) 시 refresh 쿠키로 새 access 를 재발급하는 공유 헬퍼.
// - 동시 401 dedup: 여러 요청이 동시에 401 이 나도 in-flight refresh 하나를 공유한다
//   (thundering herd 로 refresh 가 대량 발생/실패해 로그아웃되던 문제 방지).
// - 새 토큰은 localStorage(라우트별 키)에만 저장한다. 백엔드 인스턴스(_axios.js·custom.instance.js)는
//   요청마다 여기서 읽어 붙인다. raw axios(default 인스턴스) defaults 에는 절대 넣지 않는다 —
//   raw axios 는 S3·presigned URL 전용이라, 거기 Bearer 가 붙으면 S3 가 400(Unsupported
//   Authorization Type)으로 거부해 보고서 다운로드가 깨지고 토큰이 외부 origin 으로 샌다.

let _inflight = null;

export function refreshAccessToken() {
    if (_inflight) return _inflight;
    // raw axios 경유라 인터셉터의 X-Subdomain 이 안 붙는다 — 테넌트 힌트를 직접 첨부 (백엔드 계약 §1)
    const _subdomain = process.env.VUE_APP_SUBDOMAIN || getTenantKey();
    _inflight = axios
        .post(`${process.env.VUE_APP_BACKENDHOST}/v2/auth/refresh`, {}, {
            withCredentials: true,
            headers: _subdomain ? { 'X-Subdomain': _subdomain } : {},
        })
        .then((res) => {
            const _t = res && res.data && res.data.data && res.data.data.accessToken;
            if (!_t) throw new Error('refresh: no accessToken in response');
            localStorage.setItem(getAccesstokenNameByRoute(), _t);
            return _t;
        })
        .finally(() => { _inflight = null; });
    return _inflight;
}

// refresh 로 받은 새 access 토큰을 백엔드 API origin 이외로 재전송하지 않도록 제한한다.
// config.url 이 절대 URL(예: S3·외부 origin)이거나 baseURL 이 백엔드가 아니면
// refresh→재시도 대상에서 제외한다(토큰 유출 방지). origin 판별 불가 시 보수적으로 제외.
function _isBackendOriginRequest(config) {
    try {
        const _backendOrigin = new URL(process.env.VUE_APP_BACKENDHOST).origin;
        const _base = (config && config.baseURL) || process.env.VUE_APP_BACKENDHOST;
        const _resolved = new URL((config && config.url) || '', _base);
        return _resolved.origin === _backendOrigin;
    } catch (_e) {
        return false;
    }
}

// 비인증(public) 승인 엔드포인트 목록. 이 엔드포인트들의 401 은 "로그인 세션 만료" 가 아니라
// "승인 토큰(리소스) 자체가 무효/만료" 를 뜻한다. 따라서 refresh(재발급) 대상에서 제외하고,
// 글로벌 로그아웃(홈 리다이렉트) 폴백도 건너뛰어 원 401 을 컴포넌트로 전파해 화면 안내문을 띄운다.
// (register-auth 화면은 비로그인 상태로 접근하므로 refresh 를 시도해봤자 또 401 이 나고,
//  글로벌 폴백이 alert + 홈 리다이렉트로 안내문을 덮어버리던 문제를 닫는다.)
const PUBLIC_AUTH_EXEMPT_PATHS = ['/register/authorization'];

// 이 요청이 위 public(비인증) 승인 엔드포인트에 대한 것인지 판단.
// 인터셉터의 refresh 판정과 로그아웃 폴백 양쪽에서 공유한다.
export function isPublicAuthExemptEndpoint(config) {
    const _url = config && typeof config.url === 'string' ? config.url : '';
    return PUBLIC_AUTH_EXEMPT_PATHS.some((p) => _url.indexOf(p) !== -1);
}

// 이 401 이 refresh→재시도 대상인지 판단. refresh/login 엔드포인트 자체(재귀 방지),
// public 승인 엔드포인트, 이미 한 번 재시도한 요청, 백엔드 origin 이 아닌 요청은 제외한다.
export function isRefreshableAuthError(config) {
    const _url = config && typeof config.url === 'string' ? config.url : '';
    const _isAuthEndpoint =
        _url.indexOf('/v2/auth/refresh') !== -1
        || _url.indexOf('/clients/login') !== -1
        || _url.endsWith('/login');
    return !!config
        && !_isAuthEndpoint
        && !isPublicAuthExemptEndpoint(config)
        && !config._retriedWithRefresh
        && _isBackendOriginRequest(config);
}
